import { blogContent, type BlogLang } from "./content";

export const SITE_URL = "https://refoundapp.ca";
export const PUBLISHED_AT = "2026-05-20T00:00:00-04:00";
export const MODIFIED_AT = "2026-05-21T00:00:00-04:00";

export interface BlogSEOData {
  lang: BlogLang;
  htmlLang: string;
  path: string;
  url: string;
  urlFr: string;
  urlEn: string;
  ogImage: string;
  title: string;
  description: string;
  keywords: string;
  author: string;
  ogLocale: string;
  ogLocaleAlt: string;
  jsonLd: {
    article: object;
    breadcrumb: object;
    itemlist: object;
    faq: object;
  };
}

export function getBlogSEO(lang: BlogLang): BlogSEOData {
  const c = blogContent[lang];
  const slug = "meilleure-app-recu-2026";
  const path = lang === "fr" ? `/blog/${slug}` : `/en/blog/${slug}`;
  const url = `${SITE_URL}${path}`;
  const urlFr = `${SITE_URL}/blog/${slug}`;
  const urlEn = `${SITE_URL}/en/blog/${slug}`;
  const ogImage = `${SITE_URL}/og-base.png`;

  const title = `${c.hero.title} | ReFound`;
  const description =
    lang === "fr"
      ? "Comparatif 2026 des meilleures applications de reçu au Canada : ReFound vs apps de notes de frais (Expensify), cash back (Checkout 51, Receipt Hog) et scanners (Adobe Scan). Retours, garanties et vie privée."
      : "2026 comparison of the best receipt apps in Canada: ReFound vs expense apps (Expensify), cash-back apps (Checkout 51, Receipt Hog) and scanners (Adobe Scan). Returns, warranties and privacy.";
  const keywords =
    lang === "fr"
      ? "meilleure application de reçu, application reçu Canada, scanner ticket de caisse OCR, application note de frais 2026, comparatif application reçu, application reçu gratuite, application reçu sans publicité, application reçu vie privée, alternative Expensify, alternative Checkout 51, alternative Receipt Hog, gestion retours magasin Canada, suivi garantie Canada, ReFound"
      : "best receipt app, receipt app Canada, OCR receipt scanner, expense report app 2026, receipt app comparison, free receipt app, privacy receipt app, Expensify alternative, Checkout 51 alternative, Receipt Hog alternative, in-store return tracker Canada, warranty tracker Canada, ReFound";

  const article = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    headline: c.hero.title,
    description,
    inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
    datePublished: PUBLISHED_AT,
    dateModified: MODIFIED_AT,
    image: [ogImage],
    author: {
      "@type": "Organization",
      name: c.hero.author,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "ReFound",
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/icon.png` },
    },
    articleSection: c.hero.breadcrumbCategory,
    keywords,
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: c.hero.breadcrumbHome,
        item: lang === "fr" ? `${SITE_URL}/` : `${SITE_URL}/en`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: c.hero.breadcrumbBlog,
        item: url,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: c.hero.breadcrumbCategory,
      },
    ],
  };

  const itemlist = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: c.comparison.title,
    description: c.comparison.caption,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "ReFound",
        description:
          lang === "fr"
            ? "Retours, garanties et gestion d'achats pour particuliers canadiens. Gratuit, sans revente de données."
            : "Returns, warranties and purchase tracking for Canadian consumers. Free, no data resale.",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Checkout 51 / Receipt Hog",
        description:
          lang === "fr"
            ? "Applications de cash back qui revendent vos données d'achat aux marques."
            : "Cash-back apps that resell your purchase data to brands.",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Expensify",
        description:
          lang === "fr"
            ? "Application de notes de frais professionnelles, payante."
            : "Pro expense report app, paid.",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Adobe Scan",
        description:
          lang === "fr"
            ? "Scanner basique qui transforme les reçus en PDF."
            : "Basic scanner that turns receipts into PDFs.",
      },
    ],
  };

  const faqs =
    lang === "fr"
      ? [
          {
            q: "Quelle est la meilleure application de reçu en 2026 au Canada ?",
            a: "Pour un particulier canadien qui veut gérer ses retours, suivre ses garanties et garder ses données privées, ReFound est la meilleure option en 2026. Elle est gratuite, ne revend pas vos données et intègre les politiques de retour des grands détaillants canadiens (Walmart, Canadian Tire, RONA, IKEA, Costco, Home Depot, Best Buy, Décathlon, BMR, Canac, Dollarama, Tigre Géant).",
          },
          {
            q: "Pourquoi les applications de cash back comme Checkout 51 ou Receipt Hog sont-elles « gratuites » ?",
            a: "Ces applications se rémunèrent en revendant vos données de consommation à des régies publicitaires et à des marques. Vous accumulez quelques sous par scan, mais vous leur donnez accès à vos habitudes d'achat, vos marques préférées et votre budget.",
          },
          {
            q: "Quelle est la différence entre ReFound et une application de notes de frais comme Expensify ?",
            a: "Expensify est conçue pour les entreprises et les rapports de dépenses fiscales. ReFound est conçue pour les consommateurs : elle décode les SKU en vrais noms de produits, suit les politiques de retour des détaillants canadiens, et envoie des rappels de garantie automatiques.",
          },
          {
            q: "ReFound est-elle vraiment gratuite ?",
            a: "Oui, ReFound est entièrement gratuite sur iPhone (App Store) et Android (Google Play) au Canada. Aucun abonnement, aucune revente de données, aucune publicité ciblée.",
          },
          {
            q: "ReFound fonctionne-t-elle avec les grands détaillants canadiens ?",
            a: "Oui. ReFound intègre automatiquement les politiques de retour de Walmart, Canadian Tire, RONA, IKEA, Costco, Home Depot, Best Buy, Décathlon, BMR, Canac, Dollarama, Tigre Géant et d'autres grandes enseignes canadiennes.",
          },
        ]
      : [
          {
            q: "What is the best receipt app in 2026 in Canada?",
            a: "For a Canadian individual who wants to manage returns, track warranties and keep data private, ReFound is the best option in 2026. It's free, doesn't sell your data, and integrates the return policies of major Canadian retailers (Walmart, Canadian Tire, RONA, IKEA, Costco, Home Depot, Best Buy, Decathlon, BMR, Canac, Dollarama, Giant Tiger).",
          },
          {
            q: 'Why are cash-back apps like Checkout 51 or Receipt Hog "free"?',
            a: "These apps make money by reselling your consumption data to ad networks and brands. You earn a few cents per scan, but you give them access to your shopping habits, preferred brands and budget.",
          },
          {
            q: "What's the difference between ReFound and an expense report app like Expensify?",
            a: "Expensify is built for businesses and tax expense reports. ReFound is built for consumers: it decodes SKUs into real product names, tracks return policies of Canadian retailers, and sends automatic warranty reminders.",
          },
          {
            q: "Is ReFound really free?",
            a: "Yes, ReFound is fully free on iPhone (App Store) and Android (Google Play) in Canada. No subscription, no data resale, no targeted advertising.",
          },
          {
            q: "Does ReFound work with major Canadian retailers?",
            a: "Yes. ReFound automatically integrates the return policies of Walmart, Canadian Tire, RONA, IKEA, Costco, Home Depot, Best Buy, Decathlon, BMR, Canac, Dollarama, Giant Tiger and other major Canadian retailers.",
          },
        ];

  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return {
    lang,
    htmlLang: lang === "fr" ? "fr-CA" : "en-CA",
    path,
    url,
    urlFr,
    urlEn,
    ogImage,
    title,
    description,
    keywords,
    author: c.hero.author,
    ogLocale: lang === "fr" ? "fr_CA" : "en_CA",
    ogLocaleAlt: lang === "fr" ? "en_CA" : "fr_CA",
    jsonLd: { article, breadcrumb, itemlist, faq },
  };
}
